import React, { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SwapResolvedMessage } from '../../../components';
import { ButtonSizes, SKButton } from '../../../components/SwapKiwi/Button';
import { AlertColor, AlertComp } from '../../../components/SwapKiwi/Alert';
import { NFTSwapContainer } from '../../../components/SwapKiwi/NFTSwapContainer';
import { SKInput } from '../../../components/SwapKiwi/Input';
import { Routes } from '../../../constants/routes';
import {
  selectAppState,
  selectSelectedNFTs,
  selectSwapResolvedData,
  selectEnteredEthAmount,
  selectRecipient,
} from '../../../ducks/app/selectors';
import { PageHeader } from '../../../components/SwapKiwi/PageHeader';
import { IconComp } from '../../../components/SwapKiwi/IconComponent';
import { ReactComponent as StatusIndicator } from '../../../assets/svg/icons/status-indicator-step-2.svg';
import { ReactComponent as StatusIndicatorAllGood } from '../../../assets/svg/icons/status-indicator-step-3.svg';
import { ReactComponent as ArrowLeft } from '../../../assets/svg/icons/arrow-left.svg';
import { ReactComponent as Wallet } from '../../../assets/svg/icons/wallet.svg';
import { ReactComponent as Refresh } from '../../../assets/svg/icons/reload.svg';
import { CreateSwapButton } from '../../../components/SwapKiwi/CreateSwapButton';
import { BadgeSizes, SKBadge } from '../../../components/SwapKiwi/Badge';
import { getIsAllApproved } from '../../../ducks/nft/selectors';
import { getFeePlusEthAmount, getIdsAndAddressesAndAmounts } from '../../../utils/nft';
import { addPendingTransaction } from '../../../ducks/transactions/actions';
import {
  addRecipient,
  AppState,
  clearSelectedNFTs,
  storeAppState,
  storeSwapResolvedData,
} from '../../../ducks/app/actions';
import { addErrorNotification } from '../../../services/notifications';
import { utils } from 'ethers';
import { useSwapContract } from '../../../hooks/contract/useContract';

import cls from 'classnames';

export const CreateSwapConfirm: FC = () => {
  const history = useHistory();
  const appState = useSelector(selectAppState);
  const allNFTsApproved = useSelector(getIsAllApproved);
  const enteredEthAmount = useSelector(selectEnteredEthAmount);
  const { type, message, annotations } = useSelector(selectSwapResolvedData);
  const dispatch = useDispatch();
  const contract = useSwapContract();

  const [createSwapLoading, setCreateSwapLoading] = useState(false);
  const selectedNFTs = useSelector(selectSelectedNFTs);
  const totalUserNFTs = selectedNFTs.reduce((previousValue, currentValue) => {
    const current = currentValue.amount && parseInt(currentValue.amount) > 0 ? parseInt(currentValue.amount) : 1;
    return previousValue + current;
  }, 0);
  const recipient = useSelector(selectRecipient);
  const onRecipientInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(addRecipient(e.target.value));
  };

  if (appState === AppState.SWAP_RESOLVED) {
    return <SwapResolvedMessage type={type} message={message} annotations={annotations} />;
  }

  const handleCreateSwap = async (): Promise<void> => {
    setCreateSwapLoading(true);
    if (recipient && contract) {
      const { addresses, ids, amounts } = getIdsAndAddressesAndAmounts(selectedNFTs);
      const fee = await contract.fee();
      try {
        utils.getAddress(recipient);
        try {
          const tx = await contract.proposeSwap(recipient, addresses, ids, amounts, {
            value: getFeePlusEthAmount(fee, enteredEthAmount),
          });
          dispatch(
            addPendingTransaction({
              tx,
              summary: 'Swap creation',
              completeMessage: 'Successfully created new swap',
              errorMessage: 'Creating new swap failed',
            }),
          );
          // TODO: Display loading popup and remove this wait
          // If user stays on the screen still display swap loading
          await tx.wait(1);
          dispatch(addRecipient(''));
          dispatch(clearSelectedNFTs());
          dispatch(storeAppState(AppState.SWAP_RESOLVED));
          dispatch(
            storeSwapResolvedData({
              type: 'success',
              message: 'The swap has been created!',
              annotations: 'toOpenSwaps',
            }),
          );
        } catch (e) {
          addErrorNotification('failed-tx', 'Failed transaction', e.message);
        }
      } catch (e) {
        addErrorNotification('invalid-eth-address', 'Invalid recipient ethereum address', e.message);
      }
    }
    setCreateSwapLoading(false);
  };
  return (
    <section className="bg-onion-50">
      <PageHeader
        background="onion"
        title="Creating Swap"
        description='<p class="text-lg text-cloud-900">Approve your NFTs. Kiwi works with an escrow contract. In order to transfer tokens on your behalf, we need your approval to do so.<br />Wait for the approval transaction to be executed before continuing. Add your counterpart’s wallet address and create the swap.</p>'
        link={{ label: 'Learn More', url: '/' }}
        stickerWidth={272}
        notTopBorder={false}
      />
      <div className="bg-onion-100 py-4 border-b border-cloud-600">
        <div className="app-container mx-auto">
          <div className="flex justify-between items-center bg-white border border-cloud-900 rounded-xl py-2 px-1.75">
            <div className="flex items-center">
              <IconComp size={60} icon={allNFTsApproved ? StatusIndicatorAllGood : StatusIndicator} />
              <div className="pl-2">
                <p className="text-sm font-bold">Step 2 of 2</p>
                <p className="text-md">Creating Swap</p>
                <p className="text-sm text-cloud-400">
                  Grant approval for your NFTs and add the counterpart&apos;s wallet address.
                </p>
              </div>
            </div>
            <div className="flex gap-x-1.5">
              <SKButton
                color="tertiary"
                size={ButtonSizes.XL}
                iconLeft={ArrowLeft}
                onClick={(): void => history.push(Routes.CREATE_SWAP)}
              >
                Back
              </SKButton>
              <CreateSwapButton handleCreateSwap={handleCreateSwap} createSwapLoading={createSwapLoading} />
            </div>
          </div>
        </div>
      </div>
      {!allNFTsApproved && (
        <AlertComp
          color={AlertColor.GOLD}
          className="border-b border-cloud-900"
          title="NFT approval required"
          description="We need to be able to transfer tokens on your behalf. You can always revoke approvals. Please click the 'Approve' button on the NFT, execute the transaction and wait until it turns green."
        />
      )}
      <div className="flex flex-wrap justify-between items-start app-container mx-auto py-9">
        <div className="user-nft-container min-w-38 max-w-43">
          <div className="flex gap-1 items-center min-h-4 mb-4 border-b border-cloud-900">
            <span className="text-display-sm mr-2">Your Offer</span>
            <SKBadge size={BadgeSizes.LG} color="outline">
              {enteredEthAmount ? enteredEthAmount : 0}
              <span>ETH</span>
            </SKBadge>
            <SKBadge size={BadgeSizes.LG} color="outline">
              {totalUserNFTs}
              <span>NFTs</span>
            </SKBadge>
          </div>
          <NFTSwapContainer NFTs={selectedNFTs} convertToGridOnXL isTransferring />
        </div>
        <div className="bg-cloud-900 w-4 h-4 mt-5 rounded-full flex items-center justify-center">
          <IconComp
            icon={Refresh}
            size={40}
            color="white"
            className={cls('stroke-current text-onion-50', {
              'animate-spin': createSwapLoading,
            })}
          />
        </div>
        <div className="user-nft-container max-w-38">
          <>
            <div className="flex gap-1 items-center min-h-4 pb-2 mb-4 border-b border-cloud-900">
              <span className="text-display-sm mr-2">Recipient</span>
            </div>
            <SKInput icon={Wallet} placeholder="Wallet address" onChange={onRecipientInput} />
            <p className="text-md text-cloud-400 mt-2">
              Make sure you added the right address. Feel free to double check the wallet you want to swap with on
              opensea or etherscan. Once you’ve created the swap and confirmed the transaction with your wallet, your
              assets will move into the kiwi escrow contract.{' '}
              <strong>Until the swap is executed, your counterpart needs to first add their NFTs.</strong> Then it is up
              to you to do the final confirmation of the swap. You can always cancel and get them back as long as they
              are in the escrow, you just need to pay the gas fees.
            </p>
          </>
        </div>
      </div>
    </section>
  );
};
